import { Dialog, DialogContent } from '@solinkcorp/solink-ui/components/Dialog';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import { useEffect, useState } from 'react';
import { useControllerState } from 'solox';
import { type Controller } from '../../../Controller';
import { LayoutDetailsStep } from './LayoutDetailsStep';
import { sdmDialogContentStyles } from './LayoutSelectorStyles';
import { type PickerStep, type StartingPoint, TemplatePickerStep } from './TemplatePickerStep';

const pickerContentClassName = cn(
  'flex max-h-[90vh] w-full flex-col overflow-hidden bg-background-surface',
);

export const LayoutSelectorDialog = ({ controller }: { controller: Controller }) => {
  const isTemplatePickerVisible = useControllerState(
    controller.state,
    (state) => state.isTemplatePickerVisible,
  );
  const isLayoutDialogVisible = useControllerState(
    controller.state,
    (state) => state.isLayoutDialogVisible,
  );

  const open = isTemplatePickerVisible || isLayoutDialogVisible;

  // Picker step state is lifted here so the shared DialogContent className can
  // react to the picker's choose/gallery width without remounting on transition
  // to the details step.
  const [pickerStep, setPickerStep] = useState<PickerStep>('choose');
  const [pickerSelected, setPickerSelected] = useState<StartingPoint>(null);
  const [pickerTemplateId, setPickerTemplateId] = useState<string | null>(null);

  useEffect(() => {
    if (!isTemplatePickerVisible) return;
    const { editingLayoutOrigin, editingLayoutTemplateId } = controller.state.current;
    if (editingLayoutOrigin === 'template') {
      setPickerStep('gallery');
      setPickerSelected('template');
      setPickerTemplateId(editingLayoutTemplateId ?? null);
    } else {
      setPickerStep('choose');
      setPickerSelected(null);
      setPickerTemplateId(null);
    }
  }, [isTemplatePickerVisible, controller]);

  const handleOpenChange = (next: boolean) => {
    if (next) return;
    if (isTemplatePickerVisible) {
      controller.hideTemplatePicker();
    } else {
      controller.displayLayoutDialog(false, undefined);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        size={isTemplatePickerVisible && pickerStep === 'gallery' ? 'm' : 's'}
        style={{ transition: 'none', animationDuration: '0s' }}
        className={cn(
          'transition-none',
          isTemplatePickerVisible ? pickerContentClassName : sdmDialogContentStyles,
        )}
      >
        {isTemplatePickerVisible ? (
          <TemplatePickerStep
            controller={controller}
            step={pickerStep}
            setStep={setPickerStep}
            selected={pickerSelected}
            setSelected={setPickerSelected}
            selectedTemplateId={pickerTemplateId}
            setSelectedTemplateId={setPickerTemplateId}
          />
        ) : isLayoutDialogVisible ? (
          <LayoutDetailsStep controller={controller} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default LayoutSelectorDialog;
